import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle,
  Users,
  Package,
  RefreshCw,
  Edit
} from 'lucide-react';
import { useDevolucion, useUpdateDevolucion } from '../../hooks/useDevoluciones';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { formatCurrency, formatDate } from '../../lib/utils';

const getEstadoBadge = (estado: string) => {
  const variants: Record<string, { variant: 'success' | 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
    pendiente: { variant: 'warning', label: 'Pendiente' },
    aprobada: { variant: 'info', label: 'Aprobada' },
    rechazada: { variant: 'danger', label: 'Rechazada' },
    completada: { variant: 'success', label: 'Completada' },
  };
  const config = variants[estado] || variants.pendiente;
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

const getTipoLabel = (tipo: string) => {
  switch (tipo) {
    case 'devolucion': return 'Devolución';
    case 'cambio': return 'Cambio';
    case 'garantia': return 'Garantía';
    default: return tipo;
  }
};

const getMetodoReembolsoLabel = (metodo: string) => {
  switch (metodo) {
    case 'efectivo': return 'Efectivo';
    case 'transferencia': return 'Transferencia bancaria';
    case 'credito': return 'Crédito en tienda';
    case 'tarjeta': return 'Tarjeta de crédito';
    default: return metodo || 'No especificado';
  }
};

export function DevolucionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const devolucionId = id ? parseInt(id) : 0;

  const { data: devolucion, isLoading } = useDevolucion(devolucionId);
  const updateDevolucion = useUpdateDevolucion();

  const handleChangeEstado = async (nuevoEstado: string) => {
    if (!confirm(`¿Cambiar estado a "${nuevoEstado}"?`)) return;
    if (!devolucion) return;

    try {
      const payload: any = {
        clienteId: devolucion.clienteId || undefined,
        clienteNombre: devolucion.clienteNombre,
        clienteDocumento: devolucion.clienteDocumento || undefined,
        clienteTelefono: devolucion.clienteTelefono || undefined,
        clienteEmail: devolucion.clienteEmail || undefined,
        motivo: devolucion.motivo,
        observaciones: devolucion.observaciones || undefined,
        tipoDevolucion: devolucion.tipoDevolucion,
        metodoReembolso:
          devolucion.tipoDevolucion === 'cambio'
            ? undefined
            : devolucion.metodoReembolso || undefined,
        estado: nuevoEstado,
        detalles: devolucion.detalles.map((d: any) => ({
          productoId: d.productoId || undefined,
          productoNombre: d.productoNombre,
          productoCodigo: d.productoCodigo || undefined,
          cantidad: d.cantidad,
          precioUnitario: Number(d.precioUnitario),
          subtotal: Number(d.subtotal),
          motivoDetalle: d.motivoDetalle || devolucion.motivo,
        })),
      };

      await updateDevolucion.mutateAsync({
        id: devolucionId,
        data: payload,
      });
    } catch (error) {
      console.error(error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando devolución...</div>
      </div>
    );
  }

  if (!devolucion) {
    return (
      <div className="text-center py-8 text-red-500">
        Devolución no encontrada
      </div>
    );
  }

  const puedeEditar = devolucion.estado === 'pendiente';
  const puedeAprobar = devolucion.estado === 'pendiente';
  const puedeRechazar = devolucion.estado === 'pendiente';
  const puedeCompletar = devolucion.estado === 'aprobada';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/devoluciones')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-800">
                {devolucion.numeroDevolucion}
              </h1>
              {getEstadoBadge(devolucion.estado)}
            </div>
            <p className="text-sm text-gray-500">
              {getTipoLabel(devolucion.tipoDevolucion)} • {formatDate(devolucion.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {puedeEditar && (
            <Button
              variant="outline"
              onClick={() => navigate(`/devoluciones/editar/${devolucion.id}`)}
            >
              <Edit size={16} className="mr-2" />
              Editar
            </Button>
          )}
          {puedeAprobar && (
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => handleChangeEstado('aprobada')}
            >
              <CheckCircle size={16} className="mr-2" />
              Aprobar
            </Button>
          )}
          {puedeRechazar && (
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => handleChangeEstado('rechazada')}
            >
              <XCircle size={16} className="mr-2" />
              Rechazar
            </Button>
          )}
          {puedeCompletar && (
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => handleChangeEstado('completada')}
            >
              <CheckCircle size={16} className="mr-2" />
              Completar
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Cliente */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Users size={20} className="text-blue-500" />
              Datos del Cliente
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Nombre</p>
                <p className="font-medium text-gray-800">{devolucion.clienteNombre}</p>
              </div>
              {devolucion.clienteDocumento && (
                <div>
                  <p className="text-sm text-gray-500">Documento</p>
                  <p className="font-medium text-gray-800">{devolucion.clienteDocumento}</p>
                </div>
              )}
              {devolucion.clienteTelefono && (
                <div>
                  <p className="text-sm text-gray-500">Teléfono</p>
                  <p className="font-medium text-gray-800">{devolucion.clienteTelefono}</p>
                </div>
              )}
              {devolucion.clienteEmail && (
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-gray-800">{devolucion.clienteEmail}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Productos */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Package size={20} className="text-blue-500" />
              Productos a Devolver
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b">
                    <th className="pb-2">Producto</th>
                    <th className="pb-2 text-center">Cantidad</th>
                    <th className="pb-2 text-right">Precio</th>
                    <th className="pb-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {devolucion.detalles?.map((detalle: any, index: number) => (
                    <tr key={index} className="text-sm">
                      <td className="py-2">
                        <p className="font-medium text-gray-800">{detalle.productoNombre}</p>
                        {detalle.motivoDetalle && (
                          <p className="text-xs text-gray-500">Motivo: {detalle.motivoDetalle}</p>
                        )}
                      </td>
                      <td className="py-2 text-center">{detalle.cantidad}</td>
                      <td className="py-2 text-right">{formatCurrency(detalle.precioUnitario)}</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(detalle.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t font-bold">
                    <td colSpan={3} className="py-2 text-right">Total</td>
                    <td className="py-2 text-right text-blue-600">{formatCurrency(devolucion.montoTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>

        {/* Columna derecha */}
        <div className="space-y-6">
          {/* Detalles */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Detalles</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Tipo</p>
                <p className="font-medium text-gray-800 flex items-center gap-2">
                  {devolucion.tipoDevolucion === 'devolucion' && <RefreshCw size={16} className="text-blue-500" />}
                  {devolucion.tipoDevolucion === 'cambio' && <RefreshCw size={16} className="text-green-500" />}
                  {getTipoLabel(devolucion.tipoDevolucion)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Motivo</p>
                <p className="font-medium text-gray-800">{devolucion.motivo}</p>
              </div>
              {devolucion.metodoReembolso && (
                <div>
                  <p className="text-sm text-gray-500">Método de reembolso</p>
                  <p className="font-medium text-gray-800">{getMetodoReembolsoLabel(devolucion.metodoReembolso)}</p>
                </div>
              )}
              {devolucion.observaciones && (
                <div>
                  <p className="text-sm text-gray-500">Observaciones</p>
                  <p className="text-sm text-gray-600">{devolucion.observaciones}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Historial */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Historial</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Creado</span>
                <span className="text-gray-700">{formatDate(devolucion.createdAt)}</span>
              </div>
              {devolucion.fechaAprobacion && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Aprobado</span>
                  <span className="text-gray-700">{formatDate(devolucion.fechaAprobacion)}</span>
                </div>
              )}
              {devolucion.fechaCompletado && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Completado</span>
                  <span className="text-gray-700">{formatDate(devolucion.fechaCompletado)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Usuario</span>
                <span className="text-gray-700">{devolucion.usuarioCreador || 'Sistema'}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}